import { useEffect } from 'react'

/**
 * Freeze the page behind whatever is on top of it.
 *
 * A phone will happily scroll the document under an open drawer or sheet: you
 * push the navigation aside to tap "Patients", the list behind moves instead,
 * and when the drawer closes you are somewhere else entirely with no idea how
 * you got there.
 *
 * Counted, not a boolean. Two things can be up at once — a sheet opened from a
 * screen reached through the drawer — and two independent save-and-restore
 * pairs clobber each other: the first to unmount puts the old value back while
 * the second still needs the lock. Only the outermost lock touches the style.
 *
 * `overflow: hidden` rather than the `position: fixed` + negative-top trick.
 * That trick is the usual answer for older iOS, but this app is full of sticky
 * furniture — the top bar, the consultation's save bar, the record's action bar
 * — and a fixed body takes the scrollport out from under `position: sticky`,
 * which sends the top bar off the top of the screen the moment the drawer
 * opens. Hiding the overflow keeps the scroll position and leaves sticky alone.
 */

let depth = 0
let release: (() => void) | null = null

function lock(): void {
  if (depth++ > 0) return

  const root = document.documentElement
  const { body } = document
  const previousBody = body.style.overflow
  const previousPad = body.style.paddingRight

  // Hiding the overflow takes a classic scrollbar away with it, and the page
  // jumps 12px wider underneath the thing you just opened. Hold its width.
  // Zero on a phone, and zero above `lg` where the shell already owns the
  // document's overflow.
  const scrollbar = window.innerWidth - root.clientWidth

  // `body` only. An `overflow` on `body` propagates to the viewport and stops
  // the page just as dead, but `html` stays the scrollport that
  // `position: sticky` resolves against. Setting it on `html` as well does
  // work — and drops the sticky top bar to its static position, 500px up the
  // page, in full view behind a scrim that is only 62% opaque.
  body.style.overflow = 'hidden'
  if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`

  release = () => {
    body.style.overflow = previousBody
    body.style.paddingRight = previousPad
  }
}

function unlock(): void {
  if (depth === 0) return
  if (--depth > 0) return
  release?.()
  release = null
}

export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return
    lock()
    return unlock
  }, [locked])
}
